import { useState } from "react";
import { formatStoredPath } from "../domain/paths";

export interface BoardRecoveryScreenProps {
  readonly boardPath: readonly string[];
  readonly issue: string;
  readonly kind: "invalid" | "newer" | "missing_asset";
  readonly onBack: () => Promise<void>;
  readonly onDownload: () => void;
  readonly onRetry: () => Promise<void>;
  readonly onSwitchWorkspace: () => void;
  readonly workspaceName: string;
}

/** Preserves an unreadable board behind an explicit read-only recovery surface. */
export function BoardRecoveryScreen({
  boardPath,
  issue,
  kind,
  onBack,
  onDownload,
  onRetry,
  onSwitchWorkspace,
  workspaceName,
}: BoardRecoveryScreenProps) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  /** Re-inspects raw disk state without writing or replacing it. */
  const retry = async (): Promise<void> => {
    setRetrying(true);
    setRetryError(null);
    try {
      await onRetry();
    } catch (caught) {
      setRetryError(caught instanceof Error ? caught.message : "The board is still unreadable.");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <main className="board-recovery" aria-labelledby="board-recovery-title">
      <p className="eyebrow">Board kept read-only</p>
      <h1 id="board-recovery-title">The raw board is safe.</h1>
      <p>
        Shotboard cannot edit <strong>{formatStoredPath(boardPath)}</strong> in{" "}
        <strong>{workspaceName}</strong> because {recoveryReason(kind)} No board files were changed.
      </p>
      <details>
        <summary>View technical details</summary>
        <code>{issue}</code>
      </details>
      {retryError === null ? null : (
        <p className="form-error" role="alert">
          {retryError}
        </p>
      )}
      <div className="welcome-actions">
        <button
          type="button"
          className="primary-action"
          disabled={retrying}
          onClick={() => void retry()}
        >
          {retrying ? "Retrying…" : "Retry"}
        </button>
        <button type="button" className="secondary-action" onClick={onDownload}>
          Download Raw JSON
        </button>
        <button
          type="button"
          className="secondary-action"
          disabled={retrying}
          onClick={() => void onBack()}
        >
          Back to Board Library
        </button>
        <button type="button" className="secondary-action" onClick={onSwitchWorkspace}>
          Switch Workspace
        </button>
      </div>
    </main>
  );
}

/** Explains one validated recovery category in direct user language. */
function recoveryReason(kind: BoardRecoveryScreenProps["kind"]): string {
  if (kind === "newer") {
    return "it was created by a newer Shotboard schema.";
  }
  if (kind === "missing_asset") {
    return "a referenced original asset is missing or no longer matches its manifest.";
  }
  return "board.json is not a valid Shotboard document.";
}
